import React, { createContext, useMemo, useState } from "react";

import {
  User,
  Assumptions,
  oneTimeCosts,
  oneTimeBenefits,
  ongoingBenefits,
  ongoingCosts,
  qualitativeBenefits,
  objective,
} from "../types/User";

// TODO: most of this context is deprecated, except for the uuid. Need to delete deprecated features.

interface UserContextProps {
  user: User;
  setUUID: (uuid: string) => void;
  setName: (name: string) => void;
  setCompany: (company: string) => void;
  setEmail: (email: string) => void;
  setObjective: (objective: objective) => void;
  setAssumptions: (assumptions: Assumptions) => void;
  setOngoingCosts: (costs: ongoingCosts[]) => void;
  setOneTimeCosts: (costs: oneTimeCosts[]) => void;
  setOneTimeBenefits: (benefits: oneTimeBenefits[]) => void;
  setOngoingBenefits: (benefits: ongoingBenefits[]) => void;
  setQualitativeBenefits: (benefits: qualitativeBenefits[]) => void;
  setOngoingCostsFlattened: (costs) => void;
  setOngoingBenefitsFlattened: (benefits: number[][]) => void;
}

const initialUser: User = {
  uuid: "",
  name: "",
  company: "",
  email: "",
  isFinished: false,
  currentScreen: 0,
  data: {
    objective: {
      description: "",
    },
    assumptions: {
      internalHurdleRate: 12,
      description: "",
    },
    costs: {
      ongoing: [],
      ongoingFlattened: [],
      oneTime: [],
    },
    quantitativeBenefits: {
      ongoing: [],
      ongoingFlattened: [],
      oneTime: [],
    },
    qualitativeBenefits: [{ category: "", benefits: [""] }],
  },
};

const UserContext = createContext<UserContextProps>({
  user: initialUser,
  setUUID: () => {},
  setName: () => {},
  setCompany: () => {},
  setEmail: () => {},
  setObjective: () => {},
  setAssumptions: () => {},
  setOngoingCosts: () => {},
  setOneTimeCosts: () => {},
  setOneTimeBenefits: () => {},
  setOngoingBenefits: () => {},
  setQualitativeBenefits: () => {},
  setOngoingCostsFlattened: () => {},
  setOngoingBenefitsFlattened: () => {},
});
//@ts-ignore
const UserProvider: React.FC = ({ children }) => {
  const [user, setUser] = useState<User>(initialUser);

  const setUUID = (uuid: string) => {
    setUser((prevUser) => ({ ...prevUser, uuid }));
  };
  const setName = (name: string) => {
    setUser((prevUser) => ({ ...prevUser, name }));
  };

  const setCompany = (company: string) => {
    setUser((prevUser) => ({ ...prevUser, company }));
  };

  const setEmail = (email: string) => {
    setUser((prevUser) => ({ ...prevUser, email }));
  };

  const setObjective = (objective: objective) => {
    setUser((prevUser) => ({
      ...prevUser,
      data: {
        ...prevUser.data,
        objective: { ...prevUser.data.objective, ...objective },
      },
    }));
  };

  const setAssumptions = (assumptions: Assumptions) => {
    setUser((prevUser) => ({
      ...prevUser,
      data: {
        ...prevUser.data,
        assumptions: { ...prevUser.data.assumptions, ...assumptions },
      },
    }));
  };

  const setOngoingCosts = (costs: ongoingCosts[]) => {
    setUser((prevUser) => ({
      ...prevUser,
      data: {
        ...prevUser.data,
        costs: { ...prevUser.data.costs, ongoing: [...costs] },
      },
    }));
  };
  // sum ongoing and one time costs

  const totalOneTimeCosts = useMemo(() => {
    const { costs } = user.data;
  
    // Check if user.data exists and is an object
    if (!costs || typeof costs !== 'object') {
      return 0; // Return a default value or handle the situation accordingly
    }
  
    // Check if costs.oneTime exists and is an array
    if (!Array.isArray(costs.oneTime)) {
      return 0; // Return a default value or handle the situation accordingly
    }
  
    const oneTimeCostsTotal = costs.oneTime.reduce((total, cost) => {
      // Check if cost.costsPerMonth exists and is an object
      if (!cost.costsPerMonth || typeof cost.costsPerMonth !== 'object') {
        return total; // Skip this cost object and continue with the next one
      }
  
      const costsPerMonthValues = Object.values(cost.costsPerMonth)
        .flatMap((year) => Object.values(year))
        .reduce((acc, value) => acc + value, 0);
  
      return total + costsPerMonthValues;
    }, 0);
  
    return oneTimeCostsTotal;
  }, [user.data.costs.oneTime]);
  
  

  const setOneTimeCosts = (costs: oneTimeCosts[]) => {
    setUser((prevUser) => ({
      ...prevUser,
      data: {
        ...prevUser.data,
        costs: { ...prevUser.data.costs, oneTime: [...costs] },
      },
    }));
  };

  const setOneTimeBenefits = (benefits: oneTimeBenefits[]) => {
    setUser((prevUser) => ({
      ...prevUser,
      data: {
        ...prevUser.data,
        quantitativeBenefits: {
          ...prevUser.data.quantitativeBenefits,
          oneTime: [...benefits],
        },
      },
    }));
  };

  const setQualitativeBenefits = (benefits: qualitativeBenefits[]) => {
    setUser((prevUser) => ({
      ...prevUser,
      data: {
        ...prevUser.data,
        qualitativeBenefits: [...benefits],
      },
    }));
  };

  const setOngoingCostsFlattened = (costs) => {
    setUser((prevUser) => ({
      ...prevUser,
      data: {
        ...prevUser.data,
        costs: { ...prevUser.data.costs, ongoingFlattened: [...costs] },
      },
    }));
  };

  const setOngoingBenefitsFlattened = (benefits: number[][]) => {
    setUser((prevUser) => ({
      ...prevUser,
      data: {
        ...prevUser.data,
        quantitativeBenefits: {
          ...prevUser.data.quantitativeBenefits,
          ongoingFlattened: [...benefits],
        },
      },
    }));
  };

  const setOngoingBenefits = (benefits: ongoingBenefits[]) => {
    setUser((prevUser) => ({
      ...prevUser,
      data: {
        ...prevUser.data,
        quantitativeBenefits: {
          ...prevUser.data.quantitativeBenefits,
          ongoing: [...benefits],
        },
      },
    }));
  };
  return (
    <UserContext.Provider
      value={{
        user,
        setUUID,
        setName,
        setCompany,
        setEmail,
        setObjective,
        setAssumptions,
        setOngoingCosts,
        setOneTimeCosts,
        setOneTimeBenefits,
        setOngoingBenefits,
        setQualitativeBenefits,
        setOngoingBenefitsFlattened,
        setOngoingCostsFlattened,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export { UserContext, UserProvider };

export function useUser() {
  return React.useContext(UserContext);
}
